import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import Shell from './Shell';
import Header from './Header';
import {useParams} from 'react-router-dom';
import LeftSidebar from './LeftSidebar';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {IconButton, Tooltip} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';

import Editor from './OdiaRtfEditor';
import {useDebouncedEffect} from '../lib/useDebouncedEffect';
import {$post, $get} from '../lib/requests';
import {useAuthRedirects, AuthContext} from '../App';
import {useContext} from 'react';
import {PdfPagination} from './PdfPagination';
import {PdfScale} from './PdfScale';
import {PdfOverlays} from './PdfOverlays';

const Heading = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  padding: 4px 16px;
  border-radius: 16px;
  background: rgba(32, 33, 36, 0.1);
`;

interface IBook {
  name?: string;
  numberOfPages?: number;
  filename?: string;
  bookid?: string;
  assignedTo?: string;
  pagesLocked?: number[];
}

export function BookViewer() {
  const authed = useAuthRedirects();
  const {bookid} = useParams();
  const [bookData, setBookData] = useState<{
    book: IBook;
    progress: {page: number; cursorLine: number; cursorCh: number} | null;
  } | null>(null);

  async function getBook() {
    if (!bookid) return;
    const res = await $get<{
      book: IBook;
      progress?: {page: number; cursorLine: number; cursorCh: number} | null;
    }>(`/api/book/${bookid}`);
    if (res && res.book) {
      setBookData({book: res.book, progress: res.progress || null});
    }
  }

  useEffect(() => {
    getBook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) return null;
  if (!bookData?.book?.bookid) return <LinearProgress />;

  return (
    <Shell
      header={<Header toolbar={<Heading>{bookData.book.name}</Heading>} />}
      leftSidebar={<LeftSidebar open={false} />}
      main={
        <BookViewerMain
          book={bookData.book}
          setBook={b => setBookData({...bookData, book: b})}
          initialProgress={bookData.progress}
        />
      }
    />
  );
}

const Container = styled.div`
  width: 100%;
  display: flex;
  align-items: stretch;
`;

const Viewer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;

  border-right: 1px rgba(32, 33, 36, 0.1) solid;
  width: 760px;
  max-width: 720px;
  min-width: 720px;
  flex: 0;
`;

const ViewerHeader = styled.div`
  height: 64px;
  border-bottom: 1px rgba(32, 33, 36, 0.1) solid;

  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 0 16px;
`;

export const Divider = styled.div`
  height: 100%;
  width: 1px;
  background: rgba(32, 33, 36, 0.1);
`;

const ViewerBody = styled.div`
  flex-grow: 1;
  height: calc(100vh - 130px);
  overflow: auto;
  padding: 16px;
  background: rgba(32, 33, 36, 0.3);

  display: flex;
  align-items: flex-start;
  justify-content: center;

  .canvas-wrapper {
    position: relative;
    display: inline-block;
    margin-top: auto;
    margin-bottom: auto;
  }
`;

const CanvasContainer = styled.div`
  line-height: 0;
`;

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex-grow: 1;
`;

const EditorLoadingContainer = styled.div`
  flex-grow: 1;
  padding-top: 63px;
`;

export function openExternal(filename?: string) {
  if (!filename) return;
  const {origin} = window.location;
  window.open(`${origin}/pdf/${filename}`);
}

function BookViewerMain({
  book,
  setBook,
  initialProgress,
}: {
  book: IBook;
  setBook: (book: IBook) => void;
  initialProgress: {page: number; cursorLine: number; cursorCh: number} | null;
}) {
  const {filename, bookid} = book;
  const pdfUrl = `/pdf/${filename}`;

  const [pageNumber, setPageNumber] = useState<number>(
    initialProgress?.page || 1
  );
  const [cursor, setCursor] = useState<{line: number; ch: number}>(
    initialProgress
      ? {line: initialProgress.cursorLine, ch: initialProgress.cursorCh}
      : {line: 0, ch: 0}
  );
  const [pdf, setPdf] = useState<IPdfDocumentProxy | null>(null);
  const [scale, setScale] = useState<number>(1);
  const [fit, setFit] = useState<string | null>('page');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const {auth} = useContext(AuthContext);

  const viewerBodyRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  const isAdmin = auth?.isAdmin;
  const isAssignedToMe = auth?.user?.email === book.assignedTo;
  const isLocked = book.pagesLocked?.includes(pageNumber) || false;
  const canEdit = (isAdmin || isAssignedToMe) && !isLocked;

  async function toggleLock() {
    if (!isAdmin) return;
    const res = await $post<{success?: boolean; pagesLocked?: number[]}>(
      '/api/admin/lock-page',
      {
        bookid,
        pageIndex: pageNumber,
        lock: !isLocked,
      }
    );
    if (res && res.success && res.pagesLocked) {
      setBook({...book, pagesLocked: res.pagesLocked});
    }
  }

  async function getPdf(): Promise<IPdfDocumentProxy | null> {
    if (!window.pdfjsLib) return null;
    const pdfDoc = await window.pdfjsLib.getDocument(pdfUrl).promise;
    setPdf(pdfDoc);
    return pdfDoc;
  }

  function getScale(viewport: {height: number; width: number}) {
    if (fit === 'page') {
      const desiredHeight = (viewerBodyRef.current?.offsetHeight || 0) - 32;
      return Math.floor((desiredHeight / viewport.height) * 100) / 100;
    }

    if (fit === 'width') {
      const desiredWidth = (viewerBodyRef.current?.offsetWidth || 0) - 32;
      return Math.floor((desiredWidth / viewport.width) * 100) / 100;
    }

    return scale;
  }

  async function renderPdf(pdfDoc: IPdfDocumentProxy | null = pdf) {
    if (!pdfDoc || !canvasContainerRef.current) return;

    const page = await pdfDoc.getPage(pageNumber);
    const viewport = page.getViewport({scale: 1});
    const finalScale = getScale(viewport);
    setScale(finalScale);
    const scaledViewport = page.getViewport({scale: finalScale});

    while (canvasContainerRef.current.firstChild) {
      if (canvasContainerRef.current.lastChild) {
        canvasContainerRef.current.removeChild(
          canvasContainerRef.current.lastChild
        );
      }
    }
    const canvas = document.createElement('canvas');
    canvasContainerRef.current.appendChild(canvas);

    const canvasContext = canvas.getContext('2d');
    canvas.height = scaledViewport.height;
    canvas.width = scaledViewport.width;

    const renderContext = {
      canvasContext,
      viewport: scaledViewport,
    };
    const renderTask = page.render(renderContext);
    await renderTask.promise;
  }

  useEffect(() => {
    if (pdf) {
      renderPdf();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, scale, fit]);

  async function getExtractedText(regenerate = false) {
    setEditorLoading(true);
    try {
      const res = await $post<{extracted: string}>('/api/getText', {
        bookid,
        pageNumber,
        regenerate,
      });
      if (res && res.extracted !== undefined) {
        setContent(res.extracted);
      }
    } finally {
      setEditorLoading(false);
    }
  }

  async function handleRegenerate() {
    getExtractedText(true);
  }

  useEffect(() => {
    getExtractedText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, book]);

  useEffect(() => {
    if (book.filename) {
      getPdf().then(pdfDoc => {
        if (pdfDoc) renderPdf(pdfDoc);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book]);

  async function saveContent() {
    if (!bookid || !content) return;

    setSaving(true);
    const payload = {
      bookid,
      pageNumber,
      content,
      cursorLine: cursor.line,
      cursorCh: cursor.ch,
    };
    try {
      await $post('/api/saveContent', payload);
    } finally {
      setSaving(false);
    }
  }

  useDebouncedEffect(
    () => {
      saveContent();
    },
    [content, cursor],
    2000
  );

  useEffect(() => {
    setSaving(true);
  }, [content, cursor]);

  function onPageChange(pageNo: number) {
    setPageNumber(pageNo);
    setCursor({line: 0, ch: 0});
  }

  function onScaleChange(val: number) {
    setScale(val);
    setFit(null);
  }

  function toggleFit() {
    if (fit === 'page') setFit('width');
    else setFit('page');
  }

  return (
    <Container>
      <Viewer>
        <ViewerHeader>
          <PdfPagination
            count={book.numberOfPages || 1}
            onChange={onPageChange}
            page={pageNumber}
            onRegenerateClick={handleRegenerate}
            locked={isLocked}
          />
          {isAdmin && (
            <Tooltip title={isLocked ? 'Unlock Page' : 'Lock Page'}>
              <IconButton
                size="small"
                onClick={toggleLock}
                color={isLocked ? 'error' : 'success'}
              >
                {isLocked ? (
                  <LockIcon fontSize="small" />
                ) : (
                  <LockOpenIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
          <Divider />
          <PdfScale
            scale={scale}
            onChange={onScaleChange}
            fit={fit}
            onFitClick={toggleFit}
          />
          <Divider />
          <IconButton size="small" onClick={() => openExternal(filename)}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </ViewerHeader>
        <ViewerBody ref={viewerBodyRef}>
          <div className="canvas-wrapper">
            <CanvasContainer ref={canvasContainerRef} />
            <PdfOverlays />
          </div>
        </ViewerBody>
      </Viewer>
      <EditorContainer>
        {editorLoading ? (
          <EditorLoadingContainer>
            <LinearProgress />
          </EditorLoadingContainer>
        ) : (
          <Editor
            initialValue={content}
            onChange={md => setContent(md)}
            saving={saving}
            readOnly={!canEdit}
            initialCursor={
              initialProgress && initialProgress.page === pageNumber
                ? {
                    line: initialProgress.cursorLine,
                    ch: initialProgress.cursorCh,
                  }
                : undefined
            }
            onCursorChange={setCursor}
          />
        )}
      </EditorContainer>
    </Container>
  );
}
